import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { API_ROUTES } from "../../routes/apiRoutes";
import { useToast } from "../../components/shared/ToastProvider";
import { useTranslation } from "react-i18next";

export const useUserRole = () => {
  return useQuery({
    queryKey: ["roles", "user-role"],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.USER_ROLE);
      return res.data?.data;
    },
  });
};

export const useRolesPagination = (page = 1, perPage = 10, searchParams = {}) => {
  return useQuery({
    queryKey: [
      "roles",
      searchParams?.sortBy,
      searchParams?.sortDesc,
      searchParams?.searchQuery,
      page,
      perPage,
    ],
    queryFn: async () => {
      const res = await api.get(
        API_ROUTES.ROLES_PAGINATION(
          page,
          perPage,
          searchParams?.searchQuery,
          searchParams?.sortBy,
          searchParams?.sortDesc,
        ),
      );
      return res.data;
    },
  });
};

export const useRoles = () => {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.ROLES);
      return res.data?.data;
    },
  });
};

export const useRoleAssignOptions = (userId) => {
  return useQuery({
    queryKey: ["roles", "assignment", userId],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.ROLE_ASSIGN, { params: { user_id: userId } });
      return res.data?.data;
    },
    enabled: !!userId,
  });
};

export const useUserCreateRoleOptions = (userId) => {
  return useQuery({
    queryKey: ["roles", "admin", userId],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.ROLES_ONBOARDING, { params: { user_id: userId } });
      return res.data?.data;
    },
  });
};

export const useRolesPreview = () => {
  return useQuery({
    queryKey: ["roles", "users"],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.ROLES_PREVIEW);
      return res?.data?.data;
    },
  });
};

export const useRoleWrite = (mode = "write") => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (data) => {
      switch (mode) {
        case "edit":
          return api.patch(API_ROUTES.ROLES, data);
        default:
          return api.post(API_ROUTES.ROLES, data);
      }
    },
    onError: (error) => {
      const status = error?.response?.status;
      let defaultMessage =
        mode === "edit" ? "rolesInSystem.editRole.error" : "rolesInSystem.addRole.error";
      if (status === 400) {
        const errors = error?.response?.data?.errors;
        if (errors) {
          const message = Object.values(errors)[0]?.[0];
          addToast(message ?? t(defaultMessage), "error");
          return;
        }
        addToast(error?.response?.data?.message ?? t(defaultMessage), "error");
      } else if (status !== 403 && status !== 409) {
        addToast(error?.response?.data?.message ?? t(defaultMessage), "error");
      }
    },
    onSuccess: () => {
      let message =
        mode === "edit" ? "rolesInSystem.editRole.success" : "rolesInSystem.addRole.success";
      addToast(t(message), "success");
      queryClient.refetchQueries({ queryKey: ["roles"] });
    },
  });
};

export const useRoleDelete = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id) => api.delete(API_ROUTES.ROLE_DELETE(id)),
    onError: (error) => {
      const status = error?.response?.status;
      if (status !== 403 && status !== 409) {
        addToast(error?.response?.data?.message ?? t("rolesInSystem.deleteRole.error"), "error");
      }
    },
    onSuccess: () => {
      addToast(t("rolesInSystem.deleteRole.success"), "success");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
};

export const useRoleAssign = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data) => api.post(API_ROUTES.ROLE_ASSIGN, data),
    onError: (error) => {
      const status = error?.response?.status;
      if (status !== 403 && status !== 409) {
        addToast(error?.response?.data?.message ?? t("userManagement.assignRole.error"), "error");
      }
    },
    onSuccess: () => {
      addToast(t("userManagement.assignRole.success"), "success");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};
